"use client";

import { Suspense } from 'react';
import { useParams } from "next/navigation";
import Link from "next/link";
import Head from "next/head";
import { motion } from "motion/react";
import BaseSchema from "@/components/seo/BaseSchema";
import FAQSchema, { rentListingsFAQs } from "@/components/seo/FAQSchema";
import BreadcrumbSchema from "@/components/seo/BreadcrumbSchema";
import { MapPin, Home, Bed, Bath, Filter, ChevronRight, AlertTriangle } from "lucide-react";

import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import Loading from "@/components/app/loading/LoadingSpinner"; // Assuming a generic loading spinner component

// TODO: Fetch actual property data from Supabase based on country
const mockProperties = [
  { id: "1", name: "Charming Villa in Paris", type: "villa", city: "Paris", state: "Ile-de-France", country: "France", price: "1,200,000", beds: 4, baths: 3, imageUrl: "/images/avatars/placeholder.png", slug: "charming-villa-paris" },
  { id: "2", name: "Modern Loft in Berlin", type: "loft", city: "Berlin", state: "Berlin", country: "Germany", price: "850,000", beds: 2, baths: 2, imageUrl: "/images/avatars/placeholder.png", slug: "modern-loft-berlin" },
  // Add more mock properties for different countries for testing
];

interface Property {
  id: string;
  name: string;
  type: string;
  city: string;
  state: string;
  country: string;
  price: string;
  beds: number;
  baths: number;
  imageUrl: string;
  slug: string;
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut" as const,
    },
  },
};

function PropertyCard({ property }: { property: Property }) {
  return (
    <motion.div variants={itemVariants} className="group">
      <Card className="overflow-hidden transition-all duration-300 ease-in-out hover:shadow-xl">
        <div className="relative">
          <img
            src={property.imageUrl || "/images/avatars/placeholder.png"}
            alt={property.name}
            className="h-56 w-full object-cover transition-transform transform-gpu duration-300 group-hover:scale-105"
          />
          <div className="absolute top-3 right-3 bg-black/50 text-white px-3 py-1 rounded-full text-xs font-semibold">
            {property.type.charAt(0).toUpperCase() + property.type.slice(1)}
          </div>
        </div>
        <CardContent className="p-4 space-y-2">
          <h3 className="text-lg font-semibold text-gray-800 truncate group-hover:text-blue-600">
            {property.name}
          </h3>
          <div className="flex items-center text-sm text-gray-500">
            <MapPin size={16} className="mr-1.5 text-gray-400" />
            {property.city}, {property.state}, {property.country}
          </div>
          <div className="flex items-center space-x-3 text-sm text-gray-600 pt-1">
            <span className="flex items-center"><Bed size={16} className="mr-1 text-blue-500" /> {property.beds} Beds</span>
            <span className="flex items-center"><Bath size={16} className="mr-1 text-blue-500" /> {property.baths} Baths</span>
          </div>
        </CardContent>
        <CardFooter className="bg-gray-50/50 px-4 py-3 flex justify-between items-center">
          <p className="text-xl font-bold text-blue-600">${property.price}</p>
                            <Button asChild size="sm" className="bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] text-white">
            {/* This link should ideally go to the correct subpage, but for filter pages, it might not be necessary or could link to a more specific filter */}
            <Link href={`/listings/${property.country.toLowerCase()}/${property.type.toLowerCase()}/${property.state.toLowerCase()}/${property.city.toLowerCase()}/${property.id}/${property.slug}/for-rent`}>
              View
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

function FilterControls() {
  // TODO: Implement actual filter logic and state management
  return (
    <motion.div 
      variants={itemVariants}
      className="bg-white p-6 rounded-lg shadow-lg mb-8 sticky top-24 z-30"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
        <div>
          <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
          <select id="propertyType" className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500">
            <option>All Types</option>
            <option>House</option>
            <option>Apartment</option>
            <option>Condo</option>
            {/* Add more types */}
          </select>
        </div>
        <div>
          <label htmlFor="priceRange" className="block text-sm font-medium text-gray-700 mb-1">Price Range</label>
          <select id="priceRange" className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500">
            <option>Any Price</option>
            <option>$0 - $500,000</option>
            <option>$500,000 - $1,000,000</option>
            {/* Add more ranges */}
          </select>
        </div>
        <div>
          <label htmlFor="beds" className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
          <select id="beds" className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500">
            <option>Any</option>
            <option>1+</option>
            <option>2+</option>
            <option>3+</option>
          </select>
        </div>
                        <Button className="w-full bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] text-white flex items-center justify-center">
          <Filter size={18} className="mr-2" />
          Apply Filters
        </Button>
      </div>
    </motion.div>
  );
}

function ListingsContent() {
  const params = useParams();
  const country = params.country as string;

  // TODO: Fetch properties based on 'country' from Supabase
  // For now, filter mock data
  const filteredProperties = mockProperties.filter(
    (p) => p.country.toLowerCase() === country?.toLowerCase()
  );

  if (!country) {
    // This case should ideally be handled by Next.js routing if the param is missing
    // or by a redirect in a higher-level component/middleware.
    return (
      <div className="text-center py-10">
        <AlertTriangle size={48} className="mx-auto text-red-500" />
        <p className="mt-4 text-xl text-gray-700">Country parameter is missing.</p>
      </div>
    );
  }
  
  const breadcrumbCountry = country.charAt(0).toUpperCase() + country.slice(1);
  
  const breadcrumbItems = [
    { name: "Home", url: "https://rentwise.co" },
    { name: "Listings", url: "https://rentwise.co/listings" },
    { name: "Rent", url: "https://rentwise.co/listings/rent" },
    { name: breadcrumbCountry, url: `https://rentwise.co/listings/rent/${country}` }
  ];

  return (
    <>
      <Head>
        <title>{`Properties for Rent in ${breadcrumbCountry} | RentWise`}</title>
        <meta name="description" content={`Find properties for rent in ${breadcrumbCountry}. Browse verified listings with photos, reviews, and transparent pricing.`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://rentwise.co/listings/rent/${country}`} />
      </Head>
      
      <BaseSchema 
        pageUrl={`https://rentwise.co/listings/rent/${country}`} 
        pageTitle={`Properties for Rent in ${breadcrumbCountry}`} 
      />
      <FAQSchema faqs={rentListingsFAQs} />
      <BreadcrumbSchema items={breadcrumbItems} />
      
      <div className="min-h-screen bg-linear-to-b from-gray-50 to-blue-50/30">
      <Navbar initialAuth={{ isAuthenticated: false, userType: null }} />
      <main className="container mx-auto px-4 py-8">
        <motion.div initial="hidden" animate="visible" variants={containerVariants}>
          <motion.div variants={itemVariants} className="mb-8">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href="/listings">Listings</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{breadcrumbCountry}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </motion.div>

          <motion.h1 
            variants={itemVariants} 
            className="text-4xl font-bold text-gray-800 mb-4"
          >
            Properties in {breadcrumbCountry}
          </motion.h1>
          <motion.p 
            variants={itemVariants} 
            className="text-lg text-gray-600 mb-8"
          >
            Showing {filteredProperties.length} properties. Use filters to refine your search.
          </motion.p>

          <FilterControls />

          {filteredProperties.length > 0 ? (
            <motion.div 
              variants={containerVariants}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {filteredProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </motion.div>
          ) : (
            <motion.div variants={itemVariants} className="text-center py-10">
              <Home size={48} className="mx-auto text-gray-400" />
              <p className="mt-4 text-xl text-gray-700">No properties found for {breadcrumbCountry}.</p>
              <p className="text-gray-500">Try adjusting your filters or check back later.</p>
            </motion.div>
          )}
        </motion.div>
      </main>
      <Footer />
    </div>
    </>
  );
}


export default function CountryListingsPage() {
  return (
    <Suspense fallback={<Loading />}>
      <ListingsContent />
    </Suspense>
  );
}

// TODO: Implement generateStaticParams if needed for pre-rendering common country paths
// export async function generateStaticParams() {
//   // Fetch all unique countries from your database
//   // Example: const countries = await db.selectDistinct('country').from('properties');
//   // return countries.map(c => ({ country: c.country.toLowerCase() }));
//   return [{ country: "france" }, { country: "germany" }]; // Mock
// }

// TODO: Implement generateMetadata for SEO
// export async function generateMetadata({ params }: { params: { country: string }}) {
//   const country = params.country.charAt(0).toUpperCase() + params.country.slice(1);
//   return {
//     title: `Properties in ${country} | RentWise`,
//     description: `Find properties for rent or sale in ${country}. Browse listings with photos, videos, and reviews.`,
//   };
// } 